import { Injectable, InjectionToken, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

/** A single stage on a hiring pipeline — the box a candidate sits in
 *  while the recruiter works them through that step of the process.
 *  Kept intentionally small: name + colour drives the kanban column
 *  header, the remaining fields shape workflow behaviour. */
export interface PipelineStage {
  /** Stable id — server-assigned on create, preserved across template
   *  edits by case-insensitive name-match so kanban candidate positions
   *  don't collapse when a stage is renamed or reordered. */
  id: string;
  /** Column heading (e.g. "Phone screen", "Offer sent"). */
  name: string;
  /** Optional column accent — hex string. Used by the kanban view to
   *  colour-code stages; the editor picks from a curated palette so
   *  templates share the same visual vocabulary. */
  color?: string;
  /** Target time in a stage before it counts as stalled — drives the
   *  SLA / age-warning badge on kanban cards. Undefined = no SLA. */
  slaHours?: number;
  /** Free-text checklist the recruiter is prompted to complete before
   *  advancing a candidate out of this stage. Empty = no gate. */
  requiredActions: string[];
  /** Terminal stages (Hired, Rejected, Withdrawn) end the pipeline —
   *  the kanban view renders them as end-state columns and reporting
   *  treats a candidate landing here as a pipeline exit. */
  isTerminal: boolean;
}

/** A reusable hiring workflow — an ordered set of stages that can be
 *  applied to a job posting so recruiters don't rebuild the pipeline
 *  from scratch per role. Also called a "pipeline template" or
 *  "hiring workflow template" in ATS parlance. */
export interface PipelineTemplate {
  id: string;
  name: string;
  /** Short description shown under the name on the list. */
  description: string;
  /** Free-form role tags the template is intended for — engineering,
   *  sales, exec, etc. Recruiters filter by tag when picking a template
   *  for a new job. Not enforced; open vocabulary keeps this useful
   *  across sectors without a rigid taxonomy upfront. */
  roleTags: string[];
  stages: PipelineStage[];
  /** Marks the tenant's fallback template — applied to a job when the
   *  recruiter doesn't pick one explicitly. Exactly one template
   *  carries the flag at a time; the server enforces the invariant. */
  isDefault: boolean;
}

/** Payload for create/update — same shape as PipelineTemplate minus the
 *  server-assigned id. Stage ids are also server-assigned on create;
 *  on update the server preserves prior ids by name-match. */
export interface AddPipelineTemplatePayload {
  name: string;
  description: string;
  roleTags: string[];
  stages: Omit<PipelineStage, 'id'>[];
  isDefault: boolean;
}

/** Config the consuming app supplies at bootstrap. Mirrors the pattern
 *  used by ORG_CONTEXT_CONFIG / CV_UPLOAD_CONFIG / TEAM_SERVICE_CONFIG
 *  so this lib stays app-agnostic. */
export interface PipelineTemplateServiceConfig {
  /** Absolute base URL of the recruitment API — e.g.
   *  `https://api.dev.ka-boom.app/api/recruitment`. Pipeline templates
   *  live alongside jobs so they share that service's base. */
  recruitmentApiUrl: string;
}

/**
 * No factory intentionally — a factory that threw would crash any
 * injector that instantiated PipelineTemplateService without the
 * config, including test injectors that never call the service.
 * The service injects the token with `{ optional: true }` and defers
 * the throw to the point a method is actually invoked, so the missing-
 * config error still surfaces (with an actionable message) but only
 * when it's genuinely relevant.
 */
export const PIPELINE_TEMPLATE_SERVICE_CONFIG =
  new InjectionToken<PipelineTemplateServiceConfig>('PIPELINE_TEMPLATE_SERVICE_CONFIG');

/**
 * Pipeline (hiring workflow) templates for the current tenant — the
 * full lifecycle (list / add / edit / duplicate / remove / set-default).
 *
 * Talks to kaboom-recruitment's PipelineTemplatesController at
 * `${recruitmentApiUrl}/pipeline-templates`. Tenant scoping is
 * server-side via the X-Tenant-Id header (attached by the auth
 * interceptor), so no tenant parameter appears on the client contract.
 */
@Injectable({ providedIn: 'root' })
export class PipelineTemplateService {
  private readonly http = inject(HttpClient);
  // Optional inject so construction never throws — an injector that
  // has no reason to call this service (e.g. a test module for an
  // unrelated component) shouldn't crash just because the token
  // wasn't provided. The throw is deferred to the baseUrl getter,
  // which every network method routes through.
  private readonly config = inject(PIPELINE_TEMPLATE_SERVICE_CONFIG, { optional: true });

  private get baseUrl(): string {
    if (!this.config) {
      throw new Error(
        'PIPELINE_TEMPLATE_SERVICE_CONFIG is not provided. In your app config, add:\n' +
        "  { provide: PIPELINE_TEMPLATE_SERVICE_CONFIG, useValue: { recruitmentApiUrl: '<url>' } }",
      );
    }
    return `${this.config.recruitmentApiUrl}/pipeline-templates`;
  }

  /** List the current tenant's templates. */
  listTemplates(): Observable<PipelineTemplate[]> {
    return this.http.get<PipelineTemplate[]>(this.baseUrl);
  }

  /** Add a new template. Server enforces the "exactly one default per
   *  tenant" invariant — if `isDefault` is true, the prior default is
   *  cleared as part of the same request. */
  addTemplate(payload: AddPipelineTemplatePayload): Observable<PipelineTemplate> {
    return this.http.post<PipelineTemplate>(this.baseUrl, payload);
  }

  /** Update an existing template. Server preserves stage ids by
   *  case-insensitive name-match so kanban positions survive renames
   *  and reorders; renamed stages get a fresh id. */
  updateTemplate(id: string, payload: AddPipelineTemplatePayload): Observable<PipelineTemplate> {
    return this.http.put<PipelineTemplate>(`${this.baseUrl}/${id}`, payload);
  }

  /** Duplicate a template — server deep-copies stages with fresh ids,
   *  appends " (copy)" to the name, and always creates the copy as
   *  non-default. */
  duplicateTemplate(id: string): Observable<PipelineTemplate> {
    return this.http.post<PipelineTemplate>(`${this.baseUrl}/${id}/duplicate`, {});
  }

  /** Remove a template. Server refuses with a 400 if the target is the
   *  tenant's default — the UI shepherds the user into picking a new
   *  default first before this is called. */
  removeTemplate(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  // NOTE: the recruitment API exposes POST {id}/set-default for
  // promoting a template to the tenant default. It is intentionally
  // not surfaced here yet — the drawer's isDefault checkbox already
  // drives the same server-side transition via the standard update
  // handler, and there is no dedicated "promote from the list" UI
  // affordance to wire the endpoint to. Left as a note rather than
  // an unused method so the client shape can't drift silently from
  // the API contract until we have a call site.
}
